import type { Tenant } from '../../lib/tenant';
import type { Sample } from '../../shared/schemas';

interface Props {
  tenant: Tenant;
  spaceTypeId: string;
  onSpaceType(id: string): void;
  /** Stem wall wanted (shown for tenants with stemWall). */
  stemWall: boolean;
  onStemWall(on: boolean): void;
  onFile(file: File): void;
  onSample(sample: Sample): void;
  busy: boolean;
  error: string | null;
}

export function Start({ tenant, spaceTypeId, onSpaceType, stemWall, onStemWall, onFile, onSample, busy, error }: Props) {
  const { brand, copy, systems, spaceTypes, samples } = tenant.config;
  const swatches = systems.flatMap((s) => s.colors).slice(0, 8);
  const colorCount = systems.reduce((n, s) => n + s.colors.length, 0);

  const onChange = (e: Event) => {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) onFile(file);
  };

  return (
    <main class="mx-auto max-w-xl px-4 pb-12 pt-4 sm:pt-8">
      <header class="mb-10 flex items-center justify-between">
        <img src={tenant.asset(brand.logo)} alt={brand.name} class="h-8 w-auto" />
        <a href={`tel:${brand.phone.replace(/[^\d+]/g, '')}`} class="text-sm font-medium text-muted hover:text-ink">
          {brand.phone}
        </a>
      </header>

      <h1 class="text-[2.1rem] font-bold leading-[1.08] tracking-tight sm:text-5xl">{copy.headline}</h1>
      <p class="mt-3 text-lg leading-snug text-muted">{copy.subhead}</p>

      <div class="mt-7 flex -space-x-3" aria-hidden="true">
        {swatches.map((c, i) => (
          <img
            key={c.id + i}
            src={tenant.asset(c.thumb)}
            alt=""
            width={160}
            height={160}
            class="h-12 w-12 rounded-full object-cover ring-[3px] ring-white"
          />
        ))}
        <span class="grid h-12 place-items-center pl-5 text-sm font-medium text-muted">{colorCount} finishes to try</span>
      </div>

      <fieldset class="mt-8">
        <legend class="mb-2 text-sm font-semibold">What space is it?</legend>
        <div class="flex flex-wrap gap-2">
          {spaceTypes.map((s) => {
            const active = s.id === spaceTypeId;
            return (
              <label
                key={s.id}
                class={`flex min-h-11 cursor-pointer items-center rounded-full px-4 text-sm font-medium transition has-[:focus-visible]:outline-3 has-[:focus-visible]:outline-brand ${
                  active ? 'bg-ink text-white' : 'bg-paper text-ink hover:bg-line'
                }`}
              >
                <input type="radio" name="fv-space" value={s.id} checked={active} onChange={() => onSpaceType(s.id)} class="sr-only" />
                {s.label}
              </label>
            );
          })}
        </div>
      </fieldset>

      {tenant.config.stemWall && (
        <label class="mt-5 flex cursor-pointer items-center justify-between gap-4 rounded-2xl bg-paper px-4 py-3 has-[:focus-visible]:outline-3 has-[:focus-visible]:outline-brand">
          <span>
            <span class="block text-sm font-semibold">Coat the stem wall too</span>
            <span class="block text-xs text-muted">Carries the coating up the base of your walls. Takes a few seconds longer.</span>
          </span>
          <input
            type="checkbox"
            role="switch"
            checked={stemWall}
            onChange={(e) => onStemWall((e.currentTarget as HTMLInputElement).checked)}
            class="peer sr-only"
          />
          <span aria-hidden="true" class="relative h-6 w-11 shrink-0 rounded-full bg-line transition peer-checked:bg-brand">
            <span class={'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ' + (stemWall ? 'left-5.5' : 'left-0.5')} />
          </span>
        </label>
      )}

      <div class="mt-6 grid gap-3 sm:grid-cols-2">
        <label
          class={`flex min-h-14 cursor-pointer items-center justify-center gap-2 rounded-2xl bg-brand px-5 text-base font-semibold text-brand-text shadow-sm transition hover:brightness-110 has-[:focus-visible]:outline-3 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand ${busy ? 'pointer-events-none opacity-60' : ''}`}
        >
          <svg viewBox="0 0 24 24" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M12 16V4m0 0l-5 5m5-5l5 5M4 20h16" />
          </svg>
          Upload a photo
          <input type="file" accept="image/*" class="sr-only" onChange={onChange} disabled={busy} data-testid="upload-input" />
        </label>
        <label
          class={`flex min-h-14 cursor-pointer items-center justify-center gap-2 rounded-2xl bg-white px-5 text-base font-semibold text-ink ring-1 ring-line transition hover:bg-paper has-[:focus-visible]:outline-3 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand ${busy ? 'pointer-events-none opacity-60' : ''}`}
        >
          <svg viewBox="0 0 24 24" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
            <circle cx="12" cy="13" r="3.5" />
          </svg>
          Take a photo
          <input type="file" accept="image/*" capture="environment" class="sr-only" onChange={onChange} disabled={busy} />
        </label>
      </div>
      <p class="mt-3 text-sm text-muted">Tip: stand in a doorway and include the whole floor.</p>

      {error && (
        <p role="alert" class="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
          {error}
        </p>
      )}

      {samples.length > 0 && (
        <section class="mt-10" aria-labelledby="fv-samples">
          <h2 id="fv-samples" class="text-sm font-semibold">
            No photo handy? Try one of ours
          </h2>
          <div class="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {samples.map((s) => (
              <button
                key={s.id}
                type="button"
                disabled={busy}
                onClick={() => onSample(s)}
                class="group overflow-hidden rounded-2xl text-left ring-1 ring-line transition hover:ring-ink/30"
              >
                <img src={tenant.asset(s.image)} alt={s.label} loading="lazy" class="aspect-[4/3] w-full object-cover transition group-hover:scale-[1.02]" />
                <span class="block px-3 py-2 text-sm font-medium">{s.label}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
